import * as WebFont from "webfontloader";
import Sys from "./core/Sys";
import { Language } from "./core/Localization";
import SystemConfigService from "./services/SystemConfigService";

interface WebFontStyleSheet {
  languageCode: string;
  languageDescription: string;
  languageFont: string;
  cssContent: string;
}

export let BASE_FONT: string | null = null;
export let BASE_FONT_CSS: string | null = null;

function injectFontFaces(config: WebFontStyleSheet[]): string[] {
  const families: string[] = [];

  for (const styleSheet of config) {
    // CSS is already validated in Stage, so inject directly
    const styleEl = document.createElement("style");
    styleEl.setAttribute(
      "data-generated",
      `web-font-${styleSheet.languageCode}`
    );
    styleEl.setAttribute("data-language", styleSheet.languageCode);
    styleEl.setAttribute("data-font-name", styleSheet.languageFont);
    styleEl.textContent = styleSheet.cssContent;
    document.head.appendChild(styleEl);

    families.push(styleSheet.languageFont);
  }

  // Return the list of font families to load
  return families;
}

export function loadFontsForCurrentLanguage() {
  const languages: Language[] = Sys.settings.availableLanguages || [];

  // Check if languages have web font style sheets
  const webFontStyleSheets: WebFontStyleSheet[] = languages
    .filter((lang) => lang.fontFamily && lang.webFontStyling)
    .map((lang) => ({
      languageCode: lang.code,
      languageDescription: lang.description,
      languageFont: lang.fontFamily,
      cssContent: lang.webFontStyling,
    }));

  const families = injectFontFaces(webFontStyleSheets);

  // Set the base font for the current language
  setBaseFontForCurrentLanguage(webFontStyleSheets);

  // Load fonts using WebFont loader if we have families
  if (families.length > 0) {
    WebFont.load({
      custom: { families },
    });
  }
}

function setBaseFontForCurrentLanguage(
  webFontStyleSheets: WebFontStyleSheet[]
) {
  // The default font is Roboto
  const currentLang = Sys.settings.currentLanguageCode;
  const match = webFontStyleSheets.find((f) => f.languageCode === currentLang);

  BASE_FONT = match ? match.languageFont : "Roboto";
  BASE_FONT_CSS = match ? match.cssContent : null;
}

// Initialize font loading after system configuration is loaded
function initializeFontLoading() {
  const originalLoadConfig = SystemConfigService.loadConfig;

  SystemConfigService.loadConfig = async function (
    this: typeof SystemConfigService,
    ...args: Parameters<typeof originalLoadConfig>
  ) {
    const result = await originalLoadConfig.apply(this, args);
    loadFontsForCurrentLanguage();
    return result;
  };
}

// Determine when the document has been loaded.
document.onreadystatechange = () => {
  Sys.onReadyStateChange();
};

initializeFontLoading();
